// TasksPage.tsx — обновлённая версия с поддержкой логов выполнений задач, обновлением и созданием задач

import React, { useEffect, useMemo, useState } from "react";
import { Button, FloatButton, Spin, message } from "antd";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import TaskEditorModal, { TaskDraft } from "../components/TaskEditorModal.tsx";
import CitySelectorModal from "../components/CitySelectorModal.tsx";
import CalendarSidebar, {
  PrayerMap,
  RingMap,
} from "../components/CalendarSidebar.tsx";
import TaskTimeline, { TimelineRow } from "../components/TaskTimeline.tsx";
import { argbToHex } from "../utils/colors.ts";
import { useApp } from "../context/AppContext.tsx";
import { RepeatType } from "../components/TaskEditorModal.tsx";
import client from "../api_v2/client";
import "./TasksPage.css";

dayjs.locale("ru");
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface RawTask {
  id: number;
  title: string;
  note: string | null;
  time_at: string;
  color: number;
  done: 0 | 1;

  /*  ↓↓↓  добавляем  ↓↓↓  */
  remind_state: "none" | "scheduled";   // или «sent», если будете помечать
  remind_at   : string | null;          // YYYY‑MM‑DD HH:mm:ss

  repeat_type : RepeatType;
  repeat_until: string | null;
  deleted     : 0 | 1;
}


interface DoneLog {
  task_id: number;
  done_date: string; // YYYY-MM-DD
}

const iso = (d: Date | dayjs.Dayjs) => dayjs(d).format("YYYY-MM-DD");

export default function TasksPage() {
  const { user, city, prayers } = useApp();
  const [day, setDay] = useState(new Date());
  const [loading, setLoad] = useState(true);
  const [tasks, setTasks] = useState<RawTask[]>([]);
  const [logs, setLogs] = useState<DoneLog[]>([]);
  const [cityModal, setCM] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState<TaskDraft | null>(null);
  const [month, setMonth] = useState(dayjs());
  const REMIND_MIN = 5;

  useEffect(() => {
    if (city) {
      setCM(false);
      return;
    }
    const id = setTimeout(() => {
      if (!city) setCM(true);
    }, 300);
    return () => clearTimeout(id);
  }, [city]);

  useEffect(() => {
    if (!user) return setLoad(false);
    (async () => {
      setLoad(true);
      try {
        const { data: j } = await client.get("/ruznama/get_tasks.php", {
          params: { user_id: user.id },
        });
        if (j.success) {
          setTasks(j.result);
          setLogs(j.logs || []);
        }
      } catch {
        message.error("Не удалось загрузить задачи");
      } finally {
        setLoad(false);
      }
    })();
  }, [user]);

  const prayersForDay = useMemo(() => {
    if (!city || !prayers.length) return [];
    const key = dayjs(day).format("MM-DD");
    const row: any = prayers.find((p: any) => p.date.endsWith(key));
    if (!row) return [];
    return [
      { title: "УТРЕННИЙ", hhmm: row.morning },
      { title: "Восход", hhmm: row.sunrise },
      { title: "ОБЕДЕННЫЙ", hhmm: row.noon },
      { title: "АСР", hhmm: row.afternoon },
      { title: "ВЕЧЕРНИЙ", hhmm: row.sunset },
      { title: "НОЧНОЙ", hhmm: row.night },
    ];
  }, [day, prayers, city]);

  const prayerDone: PrayerMap = useMemo(() => {
    const m: PrayerMap = {};
    const selIso = iso(day);
    const now = dayjs();
    prayersForDay.forEach((p, idx) => {
      if (dayjs(`${selIso}T${p.hhmm}:00`).isBefore(now)) m[selIso] = idx + 1;
    });
    return m;
  }, [prayersForDay, day]);

  const taskFrags: RingMap = useMemo(() => {
    const map: RingMap = {};
    const monthStart = dayjs(day).startOf("month");

    for (let i = 0; i < 42; i++) {
      const d = monthStart.startOf("week").add(i, "day");
      const dateStr = iso(d);
      map[dateStr] = tasks.filter((t) => {
        if (t.deleted) return false;
        const taskTime = dayjs(t.time_at).startOf("day");

        if (t.repeat_type === 0) return iso(taskTime) === dateStr;

        const until = t.repeat_until ? dayjs(t.repeat_until) : null;
        if (until && d.isAfter(until, "day")) return false;

        switch (t.repeat_type) {
          case 1: return d.isSameOrAfter(taskTime);
          case 2: return d.isSameOrAfter(taskTime) && d.day() === taskTime.day();
          case 3: return d.isSameOrAfter(taskTime) && d.date() === taskTime.date();
          case 4: return d.isSameOrAfter(taskTime) && d.date() === taskTime.date() && d.month() === taskTime.month();
          default: return false;
        }
      }).map((t) => ({
        color: argbToHex(t.color),
        done: !!logs.find((l) => l.task_id === t.id && l.done_date === dateStr) || !!t.done,
      }));
    }
    return map;
  }, [tasks, logs, day]);

  const rows: TimelineRow[] = useMemo(() => {
    const dateIso = iso(day);
    const now = dayjs();

    const pray = prayersForDay.map<TimelineRow>(p => {
      const at = dayjs(`${dateIso}T${p.hhmm}:00`);
      return {
        id: p.title,
        label: p.title,
        at,
        isPrayer: true,
        color: "#4CAF50",
        done: at.isBefore(now),
      };
    });

    const task = tasks.filter((t) => {
      if (t.deleted) return false;
      const taskTime = dayjs(t.time_at);
      const currentDate = dayjs(day).startOf("day");

      if (t.repeat_type === 0) return iso(taskTime) === iso(currentDate);
      const until = t.repeat_until ? dayjs(t.repeat_until) : null;
      if (until && currentDate.isAfter(until, 'day')) return false;

      switch (t.repeat_type) {
        case 1: return currentDate.isSameOrAfter(taskTime);
        case 2: return currentDate.isSameOrAfter(taskTime) && currentDate.day() === taskTime.day();
        case 3: return currentDate.isSameOrAfter(taskTime) && currentDate.date() === taskTime.date();
        case 4: return currentDate.isSameOrAfter(taskTime) && currentDate.date() === taskTime.date() && currentDate.month() === taskTime.month();
        default: return false;
      }
    }).map<TimelineRow>((t) => {
      const baseTime = dayjs(t.time_at);
      const currentDate = dayjs(day);
      const at = currentDate.hour(baseTime.hour()).minute(baseTime.minute()).second(0).millisecond(0);
      const currentIso = iso(currentDate);
      return {
        id: `${t.id}-${currentIso}`,
        label: t.title,
        at,
        isPrayer: false,
        color: argbToHex(t.color),
        done: !!logs.find((l) => l.task_id === t.id && l.done_date === currentIso) || !!t.done,
      };
    });

    return [...pray, ...task].sort((a, b) => a.at.valueOf() - b.at.valueOf());
  }, [tasks, logs, prayersForDay, day]);

  const toggleDone = async (id: number, done: boolean) => {
    try {
      const date = iso(day);
      await client.post("/ruznama/update_task_done.php", {
        id,
        done: done ? 1 : 0,
        user_id: user?.id,
        date,
      });
      const entry = { task_id: id, done_date: date };
      setLogs((prev) => done ? [...prev, entry] : prev.filter((l) => l.task_id !== id || l.done_date !== date));
    } catch {
      message.error("Ошибка при обновлении выполненности");
    }
  };

  if (!user) {
    return <p style={{ color: "#fff" }}>Чтобы сохранять задачи,&nbsp;<Link to="/register">войдите</Link>.</p>;
  }

  return (
    <div className="page-wrap dark-theme">
      <main className="timeline-col">
        <div className="timeline-inner">
          {loading ? <Spin /> : (
            <TaskTimeline
              rows={rows}
              onToggleDone={toggleDone}
              onEdit={(id) => {
                const t = tasks.find((x) => x.id === id);
                if (!t) return;
                const base = dayjs(t.time_at);
           const draft: TaskDraft = {
  id: t.id,
  title: t.title,
  note: t.note ?? "",
  time: base,
  color: argbToHex(t.color),
  repeat: t.repeat_type,
  until: t.repeat_until ? dayjs(t.repeat_until) : null,
  notify: t.remind_state !== "none",
  done: false
};

                setEditorInitial(draft);
                setEditorOpen(true);
              }}
            />
          )}
          <button className="add-task-btn" onClick={() => { setEditorInitial(null); setEditorOpen(true); }}>+</button>
        </div>
      </main>

      <aside className="sidebar">
        <Button type="default" block style={{ marginBottom: 12 }} onClick={() => setCM(true)}>
          {city?.name || "Город"}
        </Button>

        <CalendarSidebar
          month={month}
          selected={dayjs(day)}
          onSelect={(d) => setDay(d.toDate())}
          onMonthChange={setMonth}
          prayerDone={prayerDone}
          taskFrags={taskFrags}
        />
      </aside>

      <CitySelectorModal open={cityModal} onClose={() => setCM(false)} />

      <TaskEditorModal
        open={editorOpen}
        initial={editorInitial}
        onCancel={() => setEditorOpen(false)}
        onSave={async (t) => {
          setEditorOpen(false);
      const updated: any = {
  id: t.id ?? 0,
  title: t.title,
  note: t.note,
  time_at: t.time.format("YYYY-MM-DD HH:mm:ss"),
  color: parseInt(t.color.replace("#", "0xFF")),
  done: 0,
  repeat_type: t.repeat,
  repeat_until: t.until ? t.until.format("YYYY-MM-DD") : null,
    remind_state: t.notify ? "scheduled" : "none",
  remind_at   : t.notify
      ? t.time.subtract(REMIND_MIN, "minute").format("YYYY-MM-DD HH:mm:ss")
      : null,
  deleted: 0,
};


          try {
            if (t.id) {
              // Обновление задачи
              const { data: j } = await client.post("/ruznama/update_task.php", {
                ...updated,
                user_id: user?.id,
              });
              if (!j.success) throw new Error("Ошибка при обновлении");
              setTasks(prev => prev.map((x) => x.id === updated.id ? updated : x));
              message.success("Задача обновлена");
            } else {
              // Создание новой задачи
              const { data: j } = await client.post("/ruznama/save_tasks.php", {
                user_id: user?.id,
                tasks: [updated],
              });
              if (!j.success || !j.result?.length) throw new Error("Ошибка при создании");
              updated.id = j.result[0].id;
              setTasks(prev => [...prev, updated]);
              message.success("Задача создана");
            }
          } catch {
            message.error("Ошибка при сохранении");
          }
        }}
      />
    </div>
  );
}
